import { Link } from 'react-router-dom';
import { PageSection } from '../components/page_section';

const paragraphClassName = 'mt-4 leading-7 text-aws-slate';
const headingClassName = 'mt-8 text-2xl font-black text-qs-navy';
const listClassName = 'mt-4 list-disc space-y-1 pl-6 leading-7 text-aws-slate';

export function PrivacyPolicyPage() {
  return (
    <div className="app-gradient min-h-[calc(100dvh-3.5rem)] py-12">
      <PageSection className="max-w-3xl">
        <article className="surface-card p-6 sm:p-8">
          <p className="text-xs font-black uppercase tracking-[0.2em] text-qs-indigo">
            Legal
          </p>
          <h1 className="mt-2 text-3xl font-black text-qs-navy">Privacy Notice</h1>
          <p className="mt-5 leading-7 text-aws-slate">Last updated: [DATE]</p>
          <p className={paragraphClassName}>
            This Privacy Notice explains how personal information displayed on the Patriots Creator
            Combine performance dashboard is handled.
          </p>
          <p className={paragraphClassName}>
            Diese Datenschutzerklärung ist auch in deutscher Sprache verfügbar. Im Falle von
            Widersprüchen zwischen der englischen und der deutschen Fassung ist die englische
            Fassung maßgeblich. / This Privacy Notice is also available in German. In the event of
            any discrepancy between the English and German versions, the English version shall
            prevail.
          </p>

          <h2 className={headingClassName}>1. About this dashboard</h2>
          <p className={paragraphClassName}>
            This website provides a performance dashboard for participants in the Patriots Creator
            Combine in Düsseldorf, Germany (the "Event").
          </p>
          <p className={paragraphClassName}>
            The dashboard is operated by Trent Vision LTD ("we", "us" or "our") on behalf of Kraft
            Sports + Entertainment (the "Event Organiser").
          </p>
          <p className={paragraphClassName}>
            The Event Organiser is responsible for organising the Event, collecting participant
            information and determining what participant information is provided to us for
            processing and display through this website.
          </p>

          <h2 className={headingClassName}>2. Our role</h2>
          <p className={paragraphClassName}>
            For personal information supplied to us by the Event Organiser for the purposes of
            operating this dashboard, the Event Organiser acts as the data controller and Trent
            Vision LTD acts as a data processor on its behalf.
          </p>
          <p className={paragraphClassName}>
            This means that the Event Organiser determines the purposes for which participant
            information is collected and processed and instructs us regarding the information to
            be displayed through the dashboard.
          </p>
          <p className={paragraphClassName}>
            We process that information on the Event Organiser's documented instructions for the
            purpose of providing and operating the dashboard.
          </p>
          <p className={paragraphClassName}>
            As Trent Vision LTD is established in the United Kingdom and not in the European Union,
            Trent Vision LTD has appointed an EU representative in accordance with Article 27 of
            the GDPR:
          </p>
          <p className={paragraphClassName}>
            [EU REPRESENTATIVE NAME]
            <br />
            [ADDRESS IN EU MEMBER STATE]
            <br />
            Email: [EU REPRESENTATIVE EMAIL]
          </p>
          <p className={paragraphClassName}>
            The EU representative serves as a point of contact for data subjects and supervisory
            authorities in relation to data processing activities carried out by Trent Vision LTD
            that are subject to the GDPR.
          </p>

          <h2 className={headingClassName}>3. Information displayed on the dashboard</h2>
          <p className={paragraphClassName}>
            Depending on the information provided to us by the Event Organiser, participant
            profiles may include:
          </p>
          <ul className={listClassName}>
            <li>participant name;</li>
            <li>profile photograph;</li>
            <li>sporting or athletic performance results;</li>
            <li>scores, times, distances and other performance measurements;</li>
            <li>rankings or leaderboard positions; and</li>
            <li>other Event-related performance information specified by the Event Organiser.</li>
          </ul>
          <p className={paragraphClassName}>
            We do not independently collect the above performance information from participants.
            Performance information and participant profile information displayed on the dashboard
            is supplied to us by, or on behalf of, the Event Organiser.
          </p>

          <h2 className={headingClassName}>
            4. Collection and publication of participant information
          </h2>
          <p className={paragraphClassName}>
            The processing and publication of participant information displayed through this
            website is based on the following lawful bases under Article 6(1) of the General Data
            Protection Regulation (GDPR):
          </p>
          <p className={paragraphClassName}>
            Consent (Art. 6(1)(a) GDPR): The publication of participant photographs and video
            footage on a publicly accessible dashboard is based on the explicit consent of each
            participant (or, for participants under 16, their parent or legal guardian). Consent is
            obtained by the Event Organiser prior to the Event and may be withdrawn at any time by
            contacting the Event Organiser.
          </p>
          <p className={paragraphClassName}>
            Performance of a contract (Art. 6(1)(b) GDPR): The display of participant names,
            performance scores, rankings and leaderboard positions is necessary for the performance
            of the participation agreement between the Event Organiser and each participant, which
            includes participation in the competitive leaderboard.
          </p>
          <p className={paragraphClassName}>
            Legitimate interests (Art. 6(1)(f) GDPR): Limited processing of participant names and
            performance results may also be based on the legitimate interest of the Event Organiser
            in promoting the Event and recognising participant achievements. The Event Organiser
            has conducted a balancing test and determined that this interest is not overridden by
            participants' rights and freedoms, taking into account the sporting context and
            participants' reasonable expectations.
          </p>
          <p className={paragraphClassName}>
            Where the Event Organiser relies on consent, participants may withdraw that consent at
            any time by contacting the Event Organiser. Withdrawal does not affect the lawfulness of
            processing prior to withdrawal. Questions regarding the lawful basis relied upon should
            be directed to the Event Organiser.
          </p>

          <h2 className={headingClassName}>5. Participants under 18</h2>
          <p className={paragraphClassName}>
            The dashboard may contain information relating to participants who are under 18 years
            of age.
          </p>
          <p className={paragraphClassName}>
            The Event Organiser is responsible for ensuring that any additional requirements
            applicable to the collection, processing and publication of information relating to
            minors have been satisfied before providing that information to us or instructing us to
            display it.
          </p>
          <p className={paragraphClassName}>
            We do not independently seek consent from minors or their parents or guardians in
            relation to Event participation or the publication of Event performance information
            unless specifically instructed to do so by the Event Organiser.
          </p>

          <h2 className={headingClassName}>
            5a. Photographs, video recordings and publicity rights
          </h2>
          <p className={paragraphClassName}>
            In addition to the requirements of the GDPR, the publication of photographs and video
            recordings of identifiable participants is subject to the German Act on Copyright in
            Works of Fine Arts and Photography (Kunsturhebergesetz, "KUG"), in particular §§ 22 and
            23 KUG (Recht am eigenen Bild).
          </p>
          <p className={paragraphClassName}>
            Under § 22 KUG, photographs and video footage in which a participant is recognisable may
            only be disseminated or publicly displayed with the express consent of the depicted
            person. For participants under 18 years of age, consent must be obtained from their
            parent or legal guardian.
          </p>
          <p className={paragraphClassName}>
            The Event Organiser is responsible for obtaining this consent from each participant (or
            their parent/guardian) prior to the Event. Consent forms must clearly specify:
          </p>
          <ul className={listClassName}>
            <li>
              the specific purposes for which photographs/video will be used (e.g., display on this
              dashboard, social media, press);
            </li>
            <li>the duration and geographic scope of the intended publication;</li>
            <li>the right to withdraw consent at any time with effect for the future; and</li>
            <li>how to exercise the right to request removal of images.</li>
          </ul>
          <p className={paragraphClassName}>
            We will not publish photographs or video recordings of any participant on this
            dashboard unless the Event Organiser has confirmed that the required consent under both
            the GDPR and § 22 KUG has been obtained. If consent is withdrawn, photographs of the
            relevant participant will be removed from the dashboard within a reasonable timeframe
            following notification by the Event Organiser.
          </p>
          <p className={paragraphClassName}>
            Participants who wish to have their photograph or video footage removed from this
            dashboard may contact the Event Organiser or us directly (see section 10 below).
          </p>

          <h2 className={headingClassName}>6. How we use participant information</h2>
          <p className={paragraphClassName}>
            We process participant information supplied by the Event Organiser only as necessary to
            provide and operate the dashboard and in accordance with the Event Organiser's
            instructions.
          </p>
          <p className={paragraphClassName}>This may include:</p>
          <ul className={listClassName}>
            <li>storing participant profiles and performance results;</li>
            <li>displaying participant information through the dashboard;</li>
            <li>calculating or displaying rankings and leaderboard positions where instructed;</li>
            <li>maintaining and securing the dashboard;</li>
            <li>correcting or removing information at the Event Organiser's instruction; and</li>
            <li>providing technical support in connection with the dashboard.</li>
          </ul>
          <p className={paragraphClassName}>We do not sell participant information.</p>
          <p className={paragraphClassName}>
            We do not use participant performance information or photographs for our own advertising
            or marketing purposes unless a separate lawful basis and appropriate permissions exist.
          </p>

          <h2 className={headingClassName}>7. Who can view the information</h2>
          <p className={paragraphClassName}>
            Participant information may be visible to members of the public.
          </p>
          <p className={paragraphClassName}>
            The Event Organiser is responsible for determining the intended audience for the
            dashboard and ensuring that participants have been appropriately informed about how
            their information will be made available.
          </p>

          <h2 className={headingClassName}>8. Service providers</h2>
          <p className={paragraphClassName}>
            We may use third-party technology and hosting providers to operate, secure and maintain
            the dashboard.
          </p>
          <p className={paragraphClassName}>
            Where these providers process personal information on our behalf, appropriate data
            protection arrangements will be used as required by applicable data protection law.
          </p>
          <p className={paragraphClassName}>
            Information may be processed or stored outside the European Economic Area or United
            Kingdom where our technology providers operate internationally. Where applicable,
            appropriate safeguards will be used for international transfers of personal
            information.
          </p>

          <h2 className={headingClassName}>9. How long information is retained</h2>
          <p className={paragraphClassName}>
            Participant information will be retained for as long as necessary to operate the
            dashboard in accordance with the Event Organiser's instructions and any applicable
            contractual or legal requirements.
          </p>
          <p className={paragraphClassName}>
            At the conclusion of the relevant services, information will be deleted or returned in
            accordance with the Event Organiser's instructions and applicable data protection
            requirements.
          </p>

          <h2 className={headingClassName}>10. Your data protection rights</h2>
          <p className={paragraphClassName}>
            Depending on the circumstances and applicable data protection law, participants may
            have rights in relation to their personal information, including rights to request
            access to, correction of or deletion of their information, to object to or restrict
            certain processing, and, where processing is based on consent, to withdraw that consent.
          </p>
          <p className={paragraphClassName}>
            Because the Event Organiser is the controller of participant information displayed
            through this dashboard, requests relating to these rights should normally be directed
            to:
          </p>
          <p className={paragraphClassName}>
            Kraft Sports + Entertainment
            <br />
            Email: [PRIVACY EMAIL / CONTACT DETAILS]
          </p>
          <p className={paragraphClassName}>
            If you contact us regarding information displayed about you, we may refer your request
            to the Event Organiser and will assist the Event Organiser in responding where required.
          </p>
          <p className={paragraphClassName}>
            If you believe information displayed about you is incorrect or should be removed, you
            may also contact us at [YOUR PRIVACY EMAIL] and we will take appropriate action in
            accordance with our obligations and the Event Organiser's instructions.
          </p>

          <h2 className={headingClassName}>11. Website technical information</h2>
          <p className={paragraphClassName}>
            Separately from the Event performance information supplied by the Event Organiser, we
            may process limited technical information necessary to operate and secure this website,
            such as IP addresses, server logs, device/browser information and security information.
          </p>
          <p className={paragraphClassName}>
            Where Trent Vision LTD determines the purposes and means of processing such information,
            Trent Vision LTD may act as a data controller in respect of that specific processing.
          </p>
          <p className={paragraphClassName}>
            We use this information only where necessary for purposes such as website operation,
            security, fraud prevention, troubleshooting and maintaining the reliability of our
            services.
          </p>
          <p className={paragraphClassName}>
            We do not use any analytics or user tracking services for this dashboard.
          </p>

          <h2 className={headingClassName}>12. Security</h2>
          <p className={paragraphClassName}>
            We take appropriate technical and organisational measures designed to protect personal
            information against unauthorised access, loss, alteration or disclosure.
          </p>
          <p className={paragraphClassName}>
            However, no internet-based service can guarantee absolute security.
          </p>

          <h2 className={headingClassName}>13. Questions and complaints</h2>
          <p className={paragraphClassName}>
            For questions concerning the collection of participant information, consent, the
            purposes for which participant information is processed, or your rights in relation to
            Event data, please contact:
          </p>
          <p className={paragraphClassName}>
            Kraft Sports + Entertainment
            <br />
            [ADDRESS]
            <br />
            [EMAIL]
          </p>
          <p className={paragraphClassName}>
            You also have the right to lodge a complaint with the competent data protection
            supervisory authority. For events taking place in North Rhine-Westphalia, the competent
            authority is:
          </p>
          <p className={paragraphClassName}>
            Landesbeauftragte für Datenschutz und Informationsfreiheit Nordrhein-Westfalen (LDI NRW)
            <br />
            Kavalleriestraße 2-4
            <br />
            40213 Düsseldorf
            <br />
            Germany
            <br />
            Email: poststelle@ldi.nrw.de
            <br />
            Website: www.ldi.nrw.de
          </p>
          <Link to="/" className="button-primary mt-8 inline-flex px-4 py-3 font-black">
            Return to athlete lookup
          </Link>
        </article>
      </PageSection>
    </div>
  );
}
